import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Divider,
  TextField,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Avatar,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Edit,
  ArrowBack,
  SmartToy,
  TrendingUp,
  Warning,
  Info,
  Schedule,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const TaskDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [feedback, setFeedback] = useState('');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showChangesDialog, setShowChangesDialog] = useState(false);

  // Mock task data
  const task = {
    id: id || '1',
    title: 'Supplier Disruption: TechComponents Inc',
    description: 'Primary supplier (TechComponents Inc) experiencing 4-week production delay due to factory equipment failure. This affects 60% of our electronics component supply.',
    status: 'pending',
    priority: 'critical',
    agent: 'Strategic Forecaster',
    created_at: '2025-10-03T10:30:00',
    risk_level: 'High',
    estimated_impact: '$120K revenue at risk',
    recommendation: {
      action: 'Switch to Alternative Supplier',
      details: 'Immediately engage Alternative Supplier B (verified capacity) with 2-week expedited shipping. This will incur additional costs but maintain delivery schedules.',
      cost: '$15K additional shipping costs',
      timeline: 'Implementation: 48 hours, Delivery: 2 weeks',
      confidence: 0.87,
    },
    analysis: {
      current_inventory: '10 days supply remaining',
      customer_orders: '$500K in pending orders',
      alternative_suppliers: 3,
      recommended_supplier: 'Alternative Supplier B',
      supplier_capacity: 'Confirmed available',
    },
    scenarios: [
      {
        name: 'Recommended Action',
        outcome: 'Maintain schedule, higher cost',
        cost: '$15K',
        risk: 'Low',
      },
      {
        name: 'Wait for Primary Supplier',
        outcome: '4-week delay, customer impact',
        cost: '$0',
        risk: 'High - $120K revenue loss',
      },
      {
        name: 'Partial Switch',
        outcome: 'Partial fulfillment, some delays',
        cost: '$8K',
        risk: 'Medium - $60K revenue loss',
      },
    ],
  };

  const handleApprove = () => {
    console.log('Approved with feedback:', feedback);
    // TODO: Call API
    setShowApproveDialog(false);
    navigate('/tasks');
  };

  const handleReject = () => {
    console.log('Rejected with reason:', feedback);
    // TODO: Call API
    setShowRejectDialog(false);
    navigate('/tasks');
  };

  const handleRequestChanges = () => {
    console.log('Requested changes:', feedback);
    // TODO: Call API
    setShowChangesDialog(false);
    navigate('/tasks');
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/tasks')}
          sx={{ mb: 2 }}
        >
          Back to Tasks
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {task.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<SmartToy />}
                label={task.agent}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={task.priority.toUpperCase()}
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                  fontWeight: 600,
                }}
              />
              <Chip
                label={`Risk: ${task.risk_level}`}
                icon={<Warning />}
                color="warning"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Description */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Situation Analysis
              </Typography>
              <Typography variant="body1" paragraph>
                {task.description}
              </Typography>
              
              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                AI Recommendation
              </Typography>
              <Alert severity="info" icon={<TrendingUp />} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {task.recommendation.action}
                </Typography>
              </Alert>
              <Typography variant="body1" paragraph>
                {task.recommendation.details}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                    <Typography variant="caption" color="text.secondary">
                      Additional Cost
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {task.recommendation.cost}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                    <Typography variant="caption" color="text.secondary">
                      AI Confidence
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {(task.recommendation.confidence * 100).toFixed(0)}%
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Scenario Comparison */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Scenario Comparison
              </Typography>
              <List>
                {task.scenarios.map((scenario, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        bgcolor: index === 0 ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {scenario.name}
                            </Typography>
                            {index === 0 && (
                              <Chip label="Recommended" size="small" color="primary" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              <strong>Outcome:</strong> {scenario.outcome}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Cost:</strong> {scenario.cost}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Risk:</strong> {scenario.risk}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < task.scenarios.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Your Decision
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => setShowApproveDialog(true)}
                    sx={{ py: 1.5 }}
                  >
                    Approve
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="warning"
                    startIcon={<Edit />}
                    onClick={() => setShowChangesDialog(true)}
                    sx={{ py: 1.5 }}
                  >
                    Request Changes
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => setShowRejectDialog(true)}
                    sx={{ py: 1.5 }}
                  >
                    Reject
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Task Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Task Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Created"
                    secondary={new Date(task.created_at).toLocaleString()}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Status"
                    secondary={<Chip label={task.status} size="small" color="warning" />}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Estimated Impact"
                    secondary={task.estimated_impact}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Timeline"
                    secondary={task.recommendation.timeline}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Analysis Details */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Analysis Details
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Current Inventory"
                    secondary={task.analysis.current_inventory}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Customer Orders"
                    secondary={task.analysis.customer_orders}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Alternative Suppliers"
                    secondary={`${task.analysis.alternative_suppliers} available`}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Recommended Supplier"
                    secondary={task.analysis.recommended_supplier}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onClose={() => setShowApproveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Recommendation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            You are about to approve this recommendation. The AI agent will proceed with the suggested action.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Feedback (Optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Add any comments or instructions..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApproveDialog(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Recommendation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Please provide a reason for rejecting this recommendation. This helps the AI learn your preferences.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Rejection"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Explain why you're rejecting this recommendation..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={!feedback}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Changes Dialog */}
      <Dialog open={showChangesDialog} onClose={() => setShowChangesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Describe what changes you'd like the AI to make to this recommendation.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Requested Changes"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="E.g., 'Consider a different supplier' or 'Provide more cost analysis'..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChangesDialog(false)}>Cancel</Button>
          <Button onClick={handleRequestChanges} variant="contained" color="warning" disabled={!feedback}>
            Request Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskDetailPage;
